import { queryOptions, skipToken, useQuery } from '@tanstack/react-query';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import { hydraUiApi } from '@/services/api';
import { environmentKeys, parseEnvironment, type WorkspaceEnvironment } from './entity';

const STALE_TIME = 60_000;

function useValidWorkspaceId(): number | null {
  const workspaceId = useWorkspaceIdFromCurrentUrl();
  return workspaceId != null && workspaceId > 0 ? workspaceId : null;
}

/** @public */
export async function getEnvironment(workspaceId: number): Promise<WorkspaceEnvironment> {
  const raw = await hydraUiApi.environments.get({
    params: { path: { environment_id: String(workspaceId) } },
  });
  return parseEnvironment(raw);
}

/** @public */
export const getEnvironmentQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: environmentKeys.current(workspaceId),
    queryFn: () => getEnvironment(workspaceId),
    staleTime: STALE_TIME,
  });

/**
 * Non-suspense environment read for the current workspace. Reads the
 * workspace id from the URL and routes to `skipToken` until it resolves —
 * the same "optional" pattern as `useDomainsOptional`, so callers that may
 * render before the workspace is in scope don't register a `(0)` slot.
 *
 * Pass `workspaceIdOverride` (e.g. `useCurrentWorkspace()?.id`) for callers that
 * render on non-numeric routes like `/workspaces/last/...`, where the URL has no
 * numeric workspace segment but the current workspace is still resolved.
 *
 * @public
 */
export function useEnvironmentOptional(workspaceIdOverride?: number) {
  const urlWorkspaceId = useValidWorkspaceId();
  const validWorkspaceId =
    workspaceIdOverride != null && workspaceIdOverride > 0 ? workspaceIdOverride : urlWorkspaceId;
  return useQuery({
    queryKey: validWorkspaceId != null ? environmentKeys.current(validWorkspaceId) : ['environment', 'no-workspace'],
    queryFn: validWorkspaceId != null ? () => getEnvironment(validWorkspaceId) : skipToken,
    staleTime: STALE_TIME,
  });
}
